import { CM_PER_M } from '../scene3d/units';
import { classifySunHours } from '../scene3d/shadow/shadowAggregator';
import { SUN_ZONES } from '../scene3d/shadow/shadowZones';

/**
 * Build a small offscreen canvas (one pixel per grid cell) coloured by sun
 * zone (#606). Drawn once per grid; the Fabric overlay then stretches it over
 * the garden bounds with smoothing for the soft "Jahresübersicht" look.
 *
 * @param {{cols:number, rows:number, sunHours:Float32Array}} grid
 * @returns {HTMLCanvasElement|null}
 */
export function buildHeatmapCanvas(grid) {
  if (!grid || typeof document === 'undefined') return null;
  const { cols, rows, sunHours } = grid;
  const canvas = document.createElement('canvas');
  canvas.width = cols;
  canvas.height = rows;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const image = ctx.createImageData(cols, rows);
  for (let i = 0; i < cols * rows; i += 1) {
    const [r, g, b] = SUN_ZONES[classifySunHours(sunHours[i])].rgb;
    const o = i * 4;
    image.data[o] = r;
    image.data[o + 1] = g;
    image.data[o + 2] = b;
    image.data[o + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
  return canvas;
}

/**
 * Draw the prepared heatmap image over the garden bounds on a Fabric render
 * context (from `after:render`). Mirrors drawViewportGrid's retina × viewport
 * transform, so it aligns with the objects at any zoom/pan.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {import('fabric').Canvas} canvas
 * @param {object} grid - aggregator grid (origin/cellSizeM/cols/rows)
 * @param {HTMLCanvasElement} image - from buildHeatmapCanvas
 * @param {Object} [options]
 * @param {number} [options.pixelsPerCm=1]
 * @param {number} [options.alpha=0.55]
 */
export function drawHeatmap(ctx, canvas, grid, image, { pixelsPerCm = 1, alpha = 0.55 } = {}) {
  if (!ctx || !canvas || !grid || !image) return;
  const vpt = canvas.viewportTransform;
  if (!vpt) return;

  const retina = typeof canvas.getRetinaScaling === 'function' ? canvas.getRetinaScaling() : 1;
  const mToPx = CM_PER_M * pixelsPerCm;
  const x = grid.origin.x * mToPx;
  const z = grid.origin.z * mToPx;
  const w = grid.cols * grid.cellSizeM * mToPx;
  const h = grid.rows * grid.cellSizeM * mToPx;

  ctx.save();
  ctx.setTransform(
    retina * vpt[0],
    retina * vpt[1],
    retina * vpt[2],
    retina * vpt[3],
    retina * vpt[4],
    retina * vpt[5]
  );
  ctx.globalAlpha = alpha;
  ctx.imageSmoothingEnabled = true; // weichgezeichnet über die Zellgrenzen
  ctx.drawImage(image, x, z, w, h);
  ctx.restore();
}
